import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import MessageIcon from "@mui/icons-material/Message";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BlockIcon from "@mui/icons-material/Block";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";

const ChatroomOverviewSection = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatroom/overview`
      );
      setData(response.data);
    } catch (err) {
      console.error("Failed to fetch overview data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography sx={{ color: "#666" }}>Failed to load overview data</Typography>
      </Box>
    );
  }

  const { stats, guestsByGender, messagesPerRoom, recentActivity, newUsersPerDay, topChatters, usernameWords } = data;

  // Calculate max for progress bars
  const maxRoomMessages = Math.max(...messagesPerRoom.map(r => parseInt(r.message_count) || 0), 1);
  const maxDailyMessages = Math.max(...recentActivity.map(d => parseInt(d.message_count) || 0), 1);

  return (
    <Box>
      {/* Refresh Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <IconButton
          onClick={fetchOverviewData}
          disabled={loading}
          sx={{
            color: "#9e9e9e",
            "&:hover": { color: "#fff", backgroundColor: "#394867" },
          }}
          title="Refresh"
        >
          <RefreshIcon
            sx={{
              animation: loading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </IconButton>
      </Box>

      {/* Main Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Online Now - Live indicator */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#1a2f1a",
              borderRadius: 2,
              border: "1px solid #4caf50",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  color: "#4caf50",
                  fontSize: 10,
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: "#4caf50", fontSize: 10, fontWeight: 600 }}>
                LIVE
              </Typography>
            </Box>
            <PeopleIcon sx={{ color: "#4caf50", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: 700 }}>
              {stats.onlineNow?.toLocaleString() || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: "#81c784" }}>
              Online Now
            </Typography>
          </Paper>
        </Grid>

        {/* Total Guests */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#ff9800", transform: "translateY(-2px)" },
            }}
            onClick={() => navigate("/admin/chatroom/guests")}
          >
            <PeopleIcon sx={{ color: "#ff9800", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.totalGuests.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Total Guests
            </Typography>
          </Paper>
        </Grid>

        {/* Claimed Users */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
            }}
          >
            <VerifiedUserIcon sx={{ color: "#4caf50", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.claimedUsers.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Claimed Users
            </Typography>
          </Paper>
        </Grid>

        {/* Banned Users */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
            }}
          >
            <BlockIcon sx={{ color: "#f44336", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.bannedUsers.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Banned Users
            </Typography>
          </Paper>
        </Grid>

        {/* Total Messages */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#2196f3", transform: "translateY(-2px)" },
            }}
            onClick={() => navigate("/admin/chatroom/messages")}
          >
            <MessageIcon sx={{ color: "#2196f3", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.totalMessages.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Total Messages
            </Typography>
          </Paper>
        </Grid>

        {/* Messages Today */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
            }}
          >
            <TrendingUpIcon sx={{ color: "#9c27b0", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.messagesToday.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Messages Today
            </Typography>
          </Paper>
        </Grid>

        {/* Active Rooms */}
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#00bcd4", transform: "translateY(-2px)" },
            }}
            onClick={() => navigate("/admin/chatroom/rooms")}
          >
            <MeetingRoomIcon sx={{ color: "#00bcd4", fontSize: 32, mb: 1 }} />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
              {stats.activeRooms}/{stats.totalRooms}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
              Active Rooms
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Second Row: Charts and Tables */}
      <Grid container spacing={2}>
        {/* Messages per Room */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
              Messages by Room
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {messagesPerRoom.map((room) => (
                <Box key={room.slug}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {room.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      {parseInt(room.message_count).toLocaleString()}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(parseInt(room.message_count) / maxRoomMessages) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: "#394867",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#ff9800",
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
              {messagesPerRoom.length === 0 && (
                <Typography sx={{ color: "#666", textAlign: "center", py: 2 }}>
                  No messages yet
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity (Last 7 Days) */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
              Activity (Last 7 Days)
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {recentActivity.map((day) => (
                <Box key={day.date}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {formatDate(day.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
                      {parseInt(day.message_count).toLocaleString()} messages
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(parseInt(day.message_count) / maxDailyMessages) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: "#394867",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#2196f3",
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
              {recentActivity.length === 0 && (
                <Typography sx={{ color: "#666", textAlign: "center", py: 2 }}>
                  No activity in the last 7 days
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Top Chatters */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
              Top Chatters (7 Days)
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontWeight: 600 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontWeight: 600 }}>
                      Username
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontWeight: 600 }}>
                      Messages
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topChatters.map((chatter, index) => (
                    <TableRow
                      key={chatter.guest_id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#1a1a2e" },
                      }}
                      onClick={() => navigate(`/admin/chatroom/user/${encodeURIComponent(chatter.username)}`)}
                    >
                      <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
                        {chatter.username}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: "1px solid #394867" }}>
                        <Chip
                          label={parseInt(chatter.message_count).toLocaleString()}
                          size="small"
                          sx={{
                            backgroundColor: index < 3 ? "#ff9800" : "#394867",
                            color: "#fff",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {topChatters.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ color: "#666", textAlign: "center", borderBottom: "1px solid #394867" }}>
                        No chat activity in the last 7 days
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Guest Demographics */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
              Guest Demographics
            </Typography>
            <Grid container spacing={2}>
              {/* Gender Distribution */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>
                  By Gender
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {guestsByGender.map((g) => (
                    <Chip
                      key={g.gender}
                      label={`${g.gender || "unknown"}: ${parseInt(g.count).toLocaleString()}`}
                      size="small"
                      sx={{
                        backgroundColor:
                          g.gender === "male" ? "#1976d2" :
                          g.gender === "female" ? "#e91e63" :
                          "#757575",
                        color: "#fff",
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Username Words */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>
                  Username Words
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {usernameWords.map((w) => (
                    <Chip
                      key={w.word_type}
                      label={`${w.word_type}: ${parseInt(w.count).toLocaleString()}`}
                      size="small"
                      sx={{
                        backgroundColor:
                          w.word_type === "adjective" ? "#9c27b0" :
                          w.word_type === "noun" ? "#00bcd4" :
                          "#757575",
                        color: "#fff",
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* New Users This Week */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1, mt: 1 }}>
                  New Users (Last 7 Days)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {newUsersPerDay.map((day) => (
                    <Box
                      key={day.date}
                      sx={{
                        textAlign: "center",
                        backgroundColor: "#1a1a2e",
                        borderRadius: 1,
                        p: 1,
                        minWidth: 60,
                      }}
                    >
                      <Typography variant="h6" sx={{ color: "#4caf50", fontWeight: 700 }}>
                        {parseInt(day.user_count)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
                        {formatDate(day.date)}
                      </Typography>
                    </Box>
                  ))}
                  {newUsersPerDay.length === 0 && (
                    <Typography sx={{ color: "#666" }}>No new users in the last 7 days</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChatroomOverviewSection;
